import React, { useState } from "react";
import { api, formatErr } from "../lib/api";
import { useCapabilities } from "../context/CapabilitiesContext";
import { Mail, Shield, UserCheck, UserX, Send, ChevronDown, ChevronUp, Crown, KeyRound } from "lucide-react";

/**
 * Inline panel rendered inside each staff card in the Staff tab of the
 * Business Dashboard. Renders the staff's current access state (no access /
 * invite pending / active / revoked) and exposes the owner-only actions:
 * invite, resend invite (= invite again), revoke access, change role.
 *
 * Backend:
 *  - POST /api/businesses/{bid}/staff/{sid}/invite
 *  - POST /api/businesses/{bid}/staff/{sid}/revoke
 *  - PATCH /api/businesses/{bid}/staff/{sid}/access
 */

const ROLE_LABELS = {
  manager: "Responsabile",
  staff: "Staff",
};

const STATUS_BADGES = {
  pending: { label: "Invito inviato", cls: "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300" },
  active: { label: "Accesso attivo", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300" },
  revoked: { label: "Revocato", cls: "bg-rose-100 text-rose-800 dark:bg-rose-950/40 dark:text-rose-300" },
};

export default function StaffAccessPanel({ staff, businessId, onChanged, canManage }) {
  const { can } = useCapabilities();
  // Capability check is the source of truth; the legacy `canManage` prop is
  // honored for backward compatibility but defaults to the live capability.
  const allowManage = canManage !== undefined ? canManage : can("staff:manage");
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(staff.access_email || "");
  const [role, setRole] = useState(staff.access_role || "staff");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const status = staff.access_status || "no_access";
  const badge = STATUS_BADGES[status];

  // Non-owners see status only — no actions. Owner-only capability mirrors
  // backend enforcement; the API will 403 anyway, this just hides the UI.
  if (!allowManage) {
    if (status === "no_access") return null;
    return (
      <div className="mt-2 border-t border-zinc-100 dark:border-zinc-800 pt-2 flex items-center gap-2 text-[11px]" data-testid={`staff-access-${staff.id}`}>
        <Shield className="h-3 w-3 text-zinc-400" />
        {badge && <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${badge.cls}`}>{badge.label}</span>}
        {staff.access_role && (
          <span className="text-zinc-500 dark:text-zinc-400">come <strong>{ROLE_LABELS[staff.access_role]}</strong></span>
        )}
      </div>
    );
  }

  async function sendInvite() {
    setErr("");
    if (!email.trim() || !email.includes("@")) {
      setErr("Inserisci una email valida.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/businesses/${businessId}/staff/${staff.id}/invite`, {
        email: email.trim().toLowerCase(),
        access_role: role,
      });
      onChanged?.();
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Errore invio invito");
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    if (!window.confirm("Revocare l'accesso a Prenotoo per " + staff.name + "?")) return;
    setBusy(true);
    try {
      await api.post(`/businesses/${businessId}/staff/${staff.id}/revoke`);
      onChanged?.();
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Errore revoca");
    } finally {
      setBusy(false);
    }
  }

  async function changeRole(newRole) {
    setBusy(true);
    try {
      await api.patch(`/businesses/${businessId}/staff/${staff.id}/access`, {
        access_role: newRole,
      });
      onChanged?.();
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Errore aggiornamento ruolo");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2 border-t border-zinc-100 dark:border-zinc-800 pt-3" data-testid={`staff-access-${staff.id}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 text-left"
        data-testid={`staff-access-toggle-${staff.id}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <Shield className="h-3.5 w-3.5 text-zinc-500 dark:text-zinc-400 shrink-0" />
          <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Accesso Prenotoo</span>
          {badge ? (
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${badge.cls}`}>{badge.label}</span>
          ) : (
            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">Nessun accesso</span>
          )}
          {status === "active" && staff.access_role && (
            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 inline-flex items-center gap-1">
              {staff.access_role === "manager" ? <Crown className="h-2.5 w-2.5" /> : <UserCheck className="h-2.5 w-2.5" />}
              {ROLE_LABELS[staff.access_role]}
            </span>
          )}
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-zinc-400" /> : <ChevronDown className="h-4 w-4 text-zinc-400" />}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {status === "no_access" || status === "revoked" || status === "pending" ? (
            <>
              <p className="text-[11px] text-zinc-500 dark:text-zinc-400 leading-relaxed">
                {status === "pending"
                  ? "Invito già inviato. Puoi reinviare o cambiare email/ruolo qui sotto."
                  : "Invia un invito email per consentire a questo operatore di accedere a Prenotoo."}
              </p>
              <div className="space-y-2">
                <div>
                  <label className="text-[10px] font-semibold tracking-widest uppercase text-zinc-500 dark:text-zinc-400">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@esempio.it"
                    className="w-full mt-1 h-9 px-3 rounded-lg bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 text-sm dark:text-zinc-100"
                    maxLength={120}
                    data-testid={`staff-access-email-${staff.id}`}
                  />
                </div>
                <div>
                  <label className="text-[10px] font-semibold tracking-widest uppercase text-zinc-500 dark:text-zinc-400">Ruolo</label>
                  <div className="mt-1 grid grid-cols-2 gap-2">
                    <RoleOption value="staff" current={role} onClick={() => setRole("staff")} testid={`role-staff-${staff.id}`}>
                      <UserCheck className="h-3.5 w-3.5" /> Staff
                    </RoleOption>
                    <RoleOption value="manager" current={role} onClick={() => setRole("manager")} testid={`role-manager-${staff.id}`}>
                      <Crown className="h-3.5 w-3.5" /> Responsabile
                    </RoleOption>
                  </div>
                </div>
                <button
                  type="button" onClick={sendInvite} disabled={busy}
                  className="h-9 px-3 rounded-full text-xs font-medium inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
                  data-testid={`staff-invite-${staff.id}`}
                >
                  <Send className="h-3.5 w-3.5" /> {status === "pending" ? "Invia di nuovo" : "Invia invito"}
                </button>
              </div>
            </>
          ) : (
            // status === "active"
            <>
              <div className="text-[11px] text-zinc-600 dark:text-zinc-300 flex items-center gap-1.5">
                <Mail className="h-3 w-3" /> {staff.access_email}
              </div>
              <div className="flex flex-wrap gap-1.5">
                <RoleOption value="staff" current={staff.access_role} onClick={() => changeRole("staff")} testid={`role-set-staff-${staff.id}`} compact>
                  Staff
                </RoleOption>
                <RoleOption value="manager" current={staff.access_role} onClick={() => changeRole("manager")} testid={`role-set-manager-${staff.id}`} compact>
                  Responsabile
                </RoleOption>
                <button
                  type="button" onClick={revoke} disabled={busy}
                  className="h-7 px-2.5 rounded-full text-[10px] font-semibold inline-flex items-center gap-1 bg-rose-100 hover:bg-rose-200 text-rose-700 dark:bg-rose-950/50 dark:hover:bg-rose-900/50 dark:text-rose-300 ml-auto"
                  data-testid={`staff-revoke-${staff.id}`}
                >
                  <UserX className="h-3 w-3" /> Revoca
                </button>
              </div>
            </>
          )}

          <RolePermissionsHelp role={role || staff.access_role || "staff"} />

          {err && <div className="text-[11px] text-rose-600 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 px-2 py-1 rounded">{err}</div>}
        </div>
      )}
    </div>
  );
}

function RoleOption({ value, current, onClick, children, testid, compact }) {
  const active = value === current;
  const cls = compact ? "h-7 px-2.5 text-[10px]" : "h-9 px-3 text-xs";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`${cls} rounded-full font-semibold inline-flex items-center gap-1 transition ${
        active
          ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900"
          : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
      }`}
    >
      {children}
    </button>
  );
}

function RolePermissionsHelp({ role }) {
  const content = role === "manager" ? (
    <>
      <strong className="text-zinc-700 dark:text-zinc-200">Responsabile</strong> — gestione completa di appuntamenti, calendario, clienti, servizi, lista d'attesa e orari staff.
      <span className="block mt-0.5 text-zinc-400 dark:text-zinc-500">Non può: fatturazione, chiavi API, documenti legali/privacy, eliminazione attività.</span>
    </>
  ) : (
    <>
      <strong className="text-zinc-700 dark:text-zinc-200">Staff</strong> — vede il proprio calendario, modifica gli appuntamenti assegnati, segna completati/cancellati.
      <span className="block mt-0.5 text-zinc-400 dark:text-zinc-500">Non può: gestire altri operatori, vedere KPI globali, modificare impostazioni.</span>
    </>
  );
  return (
    <div className="text-[10px] leading-relaxed text-zinc-500 dark:text-zinc-400 bg-zinc-50 dark:bg-zinc-900/60 border border-zinc-100 dark:border-zinc-800 rounded-lg px-2.5 py-2 flex gap-1.5">
      <KeyRound className="h-3 w-3 shrink-0 mt-0.5 text-zinc-400" />
      <div>{content}</div>
    </div>
  );
}
